/**
 * The MovesDashboardTile memo comparator, extracted so it can be unit-tested.
 *
 * OFFER STATE is the explicit exception to "updatedat covers everything" (same
 * rule as dashboardRenderGuards): assigning/unassigning a lite move creates or
 * rescinds an OFFER without touching the moves row — driver_id stays null,
 * status unchanged, updatedat not bumped. A comparator keyed on move scalars
 * alone would keep the tile's stale pill through exactly the transitions the
 * pill exists to show, so the guard also compares an offer signal derived from
 * the same scenario graph the tile renders from.
 */
import type { Moves } from '@gql/base-types';

export interface MoveTileGuardProps {
  move?: Moves;
  isAssigning?: boolean;
}

/**
 * Every live offer under the move's newest non-empty bundle, keyed `id:status`
 * and joined — ALL of them, not just the first: the tile renders the
 * accepted-first verdict offer, so a status flip on any row must re-render.
 *
 * "Newest non-empty" walks every bundle link, not just `bundletoscenarios[0]`,
 * because the query admits `ready` bundles and a freshly-created one carries no
 * offers yet: stopping at index 0 would report NO offers for a move whose older
 * dispatched bundle holds them, freezing the pill through the transitions this
 * signal exists to catch. Same rule as the report's `pickNewestOffers` and the
 * board's `toOfferInputs` — all three must agree on which bundle is the live one.
 *
 * DELIBERATELY NOT KEYED ON THE AUDIT COLUMNS. offered_at / accepted_at are stamped
 * asynchronously (db-offers fires after the status write; first-write-wins), so a
 * stamp that lands after the status change does not change this signal and does
 * not re-render the tile by itself: the pill's timestamp appears on the tile's next
 * natural re-render (a move update or the board's poll). That is the accepted
 * trade-off for not walking every audit column on each equality check.
 */
export const offerSignal = (move?: Moves): string => {
  const links = move?.scenariotomoves ?? [];
  for (const link of links) {
    for (const bundleLink of link?.scenario?.bundletoscenarios ?? []) {
      const offers = bundleLink?.bundle?.offers;
      if (offers && offers.length > 0) {
        return offers.map(offer => `${offer.id}:${offer.status}`).join(',');
      }
    }
  }
  return '';
};

/** True = props equal = skip the re-render. */
export const areMoveTilePropsEqual = (prev: MoveTileGuardProps, next: MoveTileGuardProps): boolean => {
  const moveEqual =
    prev.move?.id === next.move?.id &&
    prev.move?.status === next.move?.status &&
    prev.move?.driver_id === next.move?.driver_id &&
    prev.move?.cancel_status === next.move?.cancel_status &&
    prev.move?.consumer_pickup === next.move?.consumer_pickup &&
    prev.move?.updatedat === next.move?.updatedat &&
    offerSignal(prev.move) === offerSignal(next.move);

  return moveEqual && prev.isAssigning === next.isAssigning;
};
